// Adaptive onboarding overlay. One contextual tip on the RunScene that
// progresses based on observable game state — not a separate tutorial
// level. Targets acceptance criterion 7: a first-time player completes
// their first breed within 90 seconds without external instruction.

import Phaser from 'phaser';
import { isTouchDevice } from '../input/device';

export type OnboardingStep = 'move' | 'collect' | 'breed' | 'done';

export interface OnboardingState {
  /** Player has moved the cursor enough to count as "intentional". */
  moved: boolean;
  /** Any enemy has been killed (proves combat is happening). */
  killed: boolean;
  /** Any pickup has been collected. */
  collected: boolean;
  /** Any breed has completed. */
  bred: boolean;
}

function tipFor(step: OnboardingStep): string {
  const touch = isTouchDevice();
  switch (step) {
    case 'move':
      return touch ? 'tap and drag to steer the flock' : 'move your mouse to steer the flock';
    case 'collect':
      return 'pink hearts drop from kills — fly through them';
    case 'breed':
      return touch
        ? 'tap two fertile balls (pink ring) to breed'
        : 'pick two fertile balls (pink ring) to breed';
    case 'done':
      return '';
  }
}

export function stepFor(state: OnboardingState): OnboardingStep {
  if (state.bred) return 'done';
  if (state.collected) return 'breed';
  if (state.killed) return 'collect';
  return 'move';
}

export class OnboardingOverlay {
  private text!: Phaser.GameObjects.Text;
  private currentStep: OnboardingStep = 'move';

  constructor(private readonly scene: Phaser.Scene) {
    const { width, height } = scene.scale.gameSize;
    this.text = scene.add
      .text(width / 2, height - 48, tipFor('move'), {
        fontFamily: 'system-ui',
        fontSize: '16px',
        color: '#ff9ec4',
        backgroundColor: '#0c0d10cc',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5)
      .setDepth(900);
  }

  update(state: OnboardingState): void {
    const next = stepFor(state);
    if (next === this.currentStep) return;
    this.currentStep = next;
    if (next === 'done') {
      this.text.setVisible(false);
      return;
    }
    this.text.setText(tipFor(next));
    // Brief flash to draw attention.
    this.text.setAlpha(0);
    this.scene.tweens.add({
      targets: this.text,
      alpha: 1,
      duration: 250,
    });
  }

  destroy(): void {
    this.text.destroy();
  }
}
